/**
 * Logging Service
 * Main service for sending logs to backend 
 */

import { logQueue } from './logQueue';
import { logRetry } from './logRetry';
import { networkMonitor } from './networkMonitor';
import { getDefaultConfig, LOG_STATUSES } from './LoggingConstants';

/**
 * Logging Service Class
 * Handles all logging operations including queuing, retrying, and sending
 * 
 * @class LoggingService
 */
class LoggingService {
    /**
     * Create a LoggingService instance
     * @param {Object} dependencies - Optional dependencies for testing (Dependency Injection)
     * @param {Object} dependencies.httpClient - HTTP client for sending logs (default: axios)
     * @param {Object} dependencies.queue - Queue manager (default: logQueue)
     * @param {Object} dependencies.retry - Retry manager (default: logRetry)
     * @param {Object} dependencies.network - Network monitor (default: networkMonitor)
     */
    constructor(dependencies = {}) {
        this.isFlushing = false;
        this.flushInterval = null;
        this._lastFlushTime = 0; // Track last flush time to prevent rapid consecutive flushes
        /** When set, overrides config for enabled (e.g. via setEnabled()). */
        this._enabledOverride = undefined;

        // Dependency Injection for testability (Dependency Inversion Principle)
        this.httpClient = dependencies.httpClient || null; // Lazy load to avoid circular dependency
        this.queue = dependencies.queue || logQueue;
        this.retry = dependencies.retry || logRetry;
        this.network = dependencies.network || networkMonitor;

        this.initialize();
    }

    /** Resolved at access time so runtime config is used. */
    get enabled() {
        return this._enabledOverride !== undefined
            ? this._enabledOverride
            : getDefaultConfig().ENABLED;
    }

    /** Resolved at access time so runtime config is used. */
    get endpoint() {
        return getDefaultConfig().LOG_ENDPOINT;
    }
    
    /**
     * Get HTTP client (lazy loading to avoid circular dependency)
     * @private
     * @returns {Object} HTTP client instance
     */
    _getHttpClient() {
        if (!this.httpClient) {
            // Dynamic import to avoid circular dependency with api
            const api = require('../utils/api').default;
            this.httpClient = api;
        }
        return this.httpClient;
    }

    /**
     * Initialize the service
     * Sets up event listeners and starts background processes
     * @private
     */
    initialize() {
        if (!this.enabled) {
            return;
        }

        // Listen to network status changes (only register once)
        // Check if we already have listeners to avoid duplicates
        if (!this._networkListenerRegistered) {
            this.network.onStatusChange((online) => {
                if (online) {
                    // Add a small delay to avoid race conditions with scheduled interval
                    // and ensure network is fully ready
                    if (this._networkOnlineTimeout) {
                        clearTimeout(this._networkOnlineTimeout);
                    }
                    this._networkOnlineTimeout = setTimeout(() => {
                        if (this.network.getStatus() && !this.isFlushing) {
                            this.flushQueue();
                        }
                        this._networkOnlineTimeout = null;
                    }, 100);
                }
            });
            this._networkListenerRegistered = true;
        }

        // Listen to visibility changes (tab becomes visible)
        // Only register once to avoid duplicates
        if (!this._visibilityListenerRegistered && typeof document !== 'undefined' && document.addEventListener) {
            document.addEventListener('visibilitychange', () => {
                if (!document.hidden && this.network.getStatus() && !this.isFlushing) {
                    this.flushQueue();
                }
            });
            this._visibilityListenerRegistered = true;
        }

        // Attempt flush before page unload
        if (typeof window !== 'undefined' && window.addEventListener) {
            window.addEventListener('beforeunload', () => {
                this.flushQueueSync();
            });
        }

        // Initial flush on startup
        if (this.network.getStatus()) {
            // Use setTimeout to avoid blocking initialization
            setTimeout(() => this.flushQueue(), 0);
        }

        // Scheduled flush while online
        if (!this.flushInterval) {
            this.flushInterval = setInterval(() => {
                // Only flush if online, not already flushing, and queue is not empty
                if (this.network.getStatus() && !this.isFlushing && !this.queue.isEmpty()) {
                    this.flushQueue();
                }
            }, getDefaultConfig().RETRY_INTERVAL);
        }
    }

    /**
     * Create a log entry object
     * @param {string} actionName - Action type
     * @param {string} actionStatus - Log status
     * @param {Object} context - Additional context/metadata
     * @returns {Object} Log entry
     * @throws {Error} If actionName is invalid
     */
    createLogEntry(actionName, actionStatus = LOG_STATUSES.SUCCESS, context = {}) {
        // Validation
        if (!actionName || typeof actionName !== 'string') {
            throw new Error('actionName must be a non-empty string');
        }

        if (!Object.values(LOG_STATUSES).includes(actionStatus)) {
            throw new Error(`Invalid actionStatus: ${actionStatus}`);
        }

        // Backend can identify user from JWT token in Authorization header
        // No need to include identifier in log entry
        return {
            actionName,
            actionStatus,
            timestamp: new Date().toISOString(),
            _totalRetryCount: 0, // Track total retry attempts to prevent infinite retries
            ...(context && typeof context === 'object' && Object.keys(context).length > 0 ? { context } : {})
        };
    }

    /**
     * Log an action
     * Non-blocking operation that queues logs if offline or send fails
     * @param {string} actionName - Action type
     * @param {string} actionStatus - Log status (default: SUCCESS)
     * @param {Object} context - Additional context/metadata
     * @returns {void}
     */
    log(actionName, actionStatus = LOG_STATUSES.SUCCESS, context = {}) {
        if (!this.enabled) {
            return;
        }

        try {
            const logEntry = this.createLogEntry(actionName, actionStatus, context);

            // If online, try to send immediately
            if (this.network.getStatus()) {
                this.sendLog(logEntry).catch((error) => {
                    // sendLog already checked max retries, but verify again before queuing
                    // At this point, _totalRetryCount is still 0 (initial attempt)
                    // We'll queue it and let flushQueue handle the retry counting
                    const currentRetryCount = logEntry._totalRetryCount || 0;
                    const maxRetries = this.retry.maxRetries;
                    
                    if (currentRetryCount < maxRetries) {
                        // Silently queue if send fails - don't block user experience
                        this.queue.enqueue(logEntry);
                    } else {
                        // Discard if exceeded max retries (shouldn't happen on first attempt, but safety check)
                        if (process.env.NODE_ENV === 'development') {
                            console.warn('Log entry discarded after exceeding max retries on initial send:', logEntry);
                        }
                    }
                });
            } else {
                // If offline, queue it
                this.queue.enqueue(logEntry);
            }
        } catch (error) {
            // Log validation errors but don't throw - don't break user experience
            if (process.env.NODE_ENV === 'development') {
                console.warn('Logging error:', error);
            }
        }
    }

    /**
     * Send a single log entry to the backend
     * @param {Object} logEntry - Log entry to send
     * @returns {Promise<Object>} Promise that resolves when sent
     * @throws {Error} If sending fails after all retries
     */
    async sendLog(logEntry) {
        if (!this.enabled) {
            return Promise.resolve();
        }

        if (!logEntry || typeof logEntry !== 'object') {
            return Promise.reject(new Error('Invalid log entry'));
        }

        // Check if we've exceeded max retries before attempting to send
        const currentRetryCount = logEntry._totalRetryCount || 0;
        const maxRetries = this.retry.maxRetries;
        
        if (currentRetryCount >= maxRetries) {
            // Already exceeded max retries, don't attempt to send
            return Promise.reject(new Error(`Log entry exceeded max retries (${maxRetries})`));
        }

        const httpClient = this._getHttpClient();

        // Use minimal immediate retries (1-2) to avoid double counting with queue-based retries
        // The queue-based retry will handle the main retry logic
        const immediateRetries = 1; // Single immediate retry for transient errors

        return this.retry.executeWithRetry(
            async () => {
                const response = await httpClient.post(this.endpoint, logEntry);
                // BioWebFront's api.js interceptor returns response.data, but axios response should still have status
                // If response is unwrapped (no status), assume success (status 200-299)
                if (response && typeof response === 'object' && 'status' in response) {
                    if (response.status >= 200 && response.status < 300) {
                        return response;
                    }
                    throw new Error(`Logging failed with status: ${response.status}`);
                }
                // If response is unwrapped (response.data), assume success
                return response;
            },
            {
                maxRetries: immediateRetries, // Only 1 immediate retry
                shouldRetry: (error) => {
                    // Retry on network errors or 5xx errors
                    if (!error.response) {
                        return true; // Network error
                    }
                    const status = error.response?.status;
                    // Retry on server errors (5xx) but not client errors (4xx)
                    return status >= 500 && status < 600;
                }
            }
        );
    }

    /**
     * Flush all queued logs
     * Processes logs in batches to avoid overwhelming the server
     * @returns {Promise<void>} Promise that resolves when flush is complete
     */
    async flushQueue() {
        if (!this.enabled || this.isFlushing || !this.network.getStatus()) {
            return;
        }

        if (this.queue.isEmpty()) {
            return;
        }

        // Prevent rapid consecutive flushes (within 500ms)
        const now = Date.now();
        if (now - this._lastFlushTime < 500) {
            return;
        }

        // Set flushing flag immediately to prevent concurrent executions
        this.isFlushing = true;
        this._lastFlushTime = now;

        try {
            const BATCH_SIZE = 10;
            while (!this.queue.isEmpty() && this.network.getStatus()) {
                const batch = this.queue.dequeueBatch(BATCH_SIZE);

                if (!batch || batch.length === 0) {
                    break;
                }

                // Send batch in parallel
                const promises = batch.map(logEntry => {
                    // Create a copy to avoid race conditions with retry count
                    const entryCopy = { ...logEntry };
                    
                    // Increment retry count before attempting to send
                    // This tracks each flush attempt
                    const currentRetryCount = entryCopy._totalRetryCount || 0;
                    const maxRetries = this.retry.maxRetries;
                    
                    // Check if already exceeded max retries (shouldn't happen due to check in sendLog, but double-check for safety)
                    if (currentRetryCount >= maxRetries) {
                        if (process.env.NODE_ENV === 'development') {
                            console.warn('Log entry already exceeded max retries, discarding:', entryCopy);
                        }
                        return Promise.resolve(); // Skip this entry
                    }
                    
                    // Increment retry count for this attempt on the copy
                    entryCopy._totalRetryCount = currentRetryCount + 1;
                    
                    return this.sendLog(entryCopy).catch((error) => {
                        // sendLog already checked max retries, but verify again before re-queuing
                        const updatedRetryCount = entryCopy._totalRetryCount || 0;
                        
                        // Only re-queue if we haven't exceeded max retries
                        if (updatedRetryCount < maxRetries) {
                            // Only log errors in development
                            if (process.env.NODE_ENV === 'development') {
                                console.warn('Failed to send log entry, re-queuing (attempt', updatedRetryCount, 'of', maxRetries, '):', error);
                            }
                            
                            // Re-queue the copy with updated retry count for next flush cycle
                            this.queue.enqueue(entryCopy);
                        } else {
                            // Discard entry after max retries exceeded
                            if (process.env.NODE_ENV === 'development') {
                                console.warn('Log entry discarded after exceeding max retries (attempt', updatedRetryCount, 'of', maxRetries, '):', entryCopy);
                            }
                        }
                    });
                });

                await Promise.allSettled(promises);
            }
        } catch (error) {
            // Log errors but don't throw - don't break application
            if (process.env.NODE_ENV === 'development') {
                console.error('Error flushing log queue:', error);
            }
        } finally {
            this.isFlushing = false;
        }
    }

    /**
     * Send a log entry synchronously (for logout, beforeunload, etc.)
     * Uses sendBeacon API for reliable delivery
     * @param {string} actionName - Action type
     * @param {string} actionStatus - Log status
     * @param {Object} context - Additional context/metadata
     * @returns {boolean} True if sent successfully
     */
    logSync(actionName, actionStatus = LOG_STATUSES.SUCCESS, context = {}) {
        if (!this.enabled || !this.network.getStatus()) {
            return false;
        }

        try {
            const logEntry = this.createLogEntry(actionName, actionStatus, context);
            
            // Use sendBeacon for synchronous delivery
            if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
                const httpClient = this._getHttpClient();
                const url = httpClient.defaults?.baseURL 
                    ? `${httpClient.defaults.baseURL}${this.endpoint}`
                    : this.endpoint;
                
                const blob = new Blob([JSON.stringify(logEntry)], { type: 'application/json' });
                return navigator.sendBeacon(url, blob);
            }
            
            // Fallback: queue it if sendBeacon not available
            this.queue.enqueue(logEntry);
            return false;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Error in logSync:', error);
            }
            return false;
        }
    }

    /**
     * Synchronous flush attempt (for beforeunload)
     * Uses sendBeacon API for reliable delivery on page unload
     * @returns {void}
     */
    flushQueueSync() {
        if (!this.enabled || !this.network.getStatus() || this.queue.isEmpty()) {
            return;
        }

        // Use sendBeacon for reliable delivery on page unload
        const BATCH_SIZE = 10;
        const batch = this.queue.dequeueBatch(BATCH_SIZE);
        
        if (batch.length > 0 && typeof navigator !== 'undefined' && navigator.sendBeacon) {
            try {
                const httpClient = this._getHttpClient();
                const blob = new Blob([JSON.stringify(batch)], { type: 'application/json' });
                const baseURL = httpClient.defaults?.baseURL || '';
                const url = `${baseURL}${this.endpoint}`;
                
                const sent = navigator.sendBeacon(url, blob);
                if (!sent) {
                    // Re-queue if sendBeacon fails
                    batch.forEach(entry => this.queue.enqueue(entry));
                }
            } catch (error) {
                // Re-queue if sendBeacon fails
                batch.forEach(entry => this.queue.enqueue(entry));
            }
        }
    }

    /**
     * Enable or disable logging
     * @param {boolean} enabled - Enable status
     */
    setEnabled(enabled) {
        this._enabledOverride = enabled;
    }

    /**
     * Check if logging is enabled
     * @returns {boolean} Enabled status
     */
    isEnabled() {
        return this.enabled;
    }

    /**
     * Cleanup resources
     * Removes event listeners and clears intervals
     * @returns {void}
     */
    destroy() {
        if (this.flushInterval) {
            clearInterval(this.flushInterval);
            this.flushInterval = null;
        }
        // Don't destroy networkMonitor since it's a singleton that should persist
        // The event listeners should remain active for the lifetime of the application
        // Re-initialize to restore functionality after cleanup
        this.initialize();
    }
}

// Export singleton instance
export const loggingService = new LoggingService();
export default loggingService;
// Export class for testing
export { LoggingService };

